/**
 * The ordered outbox. Every mechanic action is queued first and sent in order — right away
 * when there's signal, later when there isn't. Files go up before the items that refer to them
 * because they were queued first. A refused item becomes a "problem" for the mechanic to see;
 * a network/server error stops the flush so order is preserved, and it retries with backoff.
 */
import { FILE_HEADERS, type SyncItem, type SyncResult } from '@wo/shared';
import { db, newClientId, type Problem, type QueueEntry } from './db';

type Listener = () => void;
const listeners = new Set<Listener>();
export const onQueueChange = (l: Listener): (() => void) => {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
};
const emit = () => listeners.forEach((l) => l());

/** Anything sent this long after it happened is treated as offline (server then uses device time). */
const OFFLINE_AFTER_MS = 30_000;

export async function queueCount(): Promise<number> {
  return (await db()).count('queue');
}
export async function problems(): Promise<Problem[]> {
  return (await db()).getAll('problems');
}
export async function clearProblem(clientId: string) {
  await (await db()).delete('problems', clientId);
  emit();
}

type ItemFields = Record<string, unknown> & { type: SyncItem['type'] };

export async function enqueueItem(userId: number, label: string, fields: ItemFields, clientId = newClientId()) {
  const item = { clientId, userId, deviceTime: new Date().toISOString(), offline: !navigator.onLine, ...fields } as SyncItem;
  await (await db()).add('queue', { clientId, userId, label, createdAt: Date.now(), item });
  emit();
  void flush();
  return clientId;
}

export async function enqueueFile(
  userId: number,
  label: string,
  blob: Blob,
  meta: { workOrderId: number; kind: 'PHOTO' | 'AUDIO' | 'SIGNATURE'; purpose: string; noteKind?: string; noteClientId?: string },
  clientId = newClientId(),
) {
  const headers: Record<string, string> = {
    [FILE_HEADERS.userId]: String(userId),
    [FILE_HEADERS.workOrderId]: String(meta.workOrderId),
    [FILE_HEADERS.kind]: meta.kind,
    [FILE_HEADERS.purpose]: meta.purpose,
    [FILE_HEADERS.deviceTime]: new Date().toISOString(),
    'content-type': blob.type || 'application/octet-stream',
  };
  if (meta.noteKind) headers[FILE_HEADERS.noteKind] = meta.noteKind;
  if (meta.noteClientId) headers[FILE_HEADERS.noteClientId] = meta.noteClientId;
  await (await db()).add('queue', { clientId, userId, label, createdAt: Date.now(), file: { clientId, blob, headers } });
  emit();
  void flush();
  return clientId;
}

let flushing: Promise<void> | null = null;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
let backoff = 5_000;
export let needsSignIn = false;

export function flush(): Promise<void> {
  flushing ??= doFlush().finally(() => (flushing = null));
  return flushing;
}

async function record(entry: QueueEntry, message: string) {
  const d = await db();
  await d.put('problems', { clientId: entry.clientId, userId: entry.userId, label: entry.label, message, at: Date.now() });
}

async function doFlush() {
  if (retryTimer) clearTimeout(retryTimer);
  const d = await db();
  try {
    for (;;) {
      const entries = await d.getAll('queue', undefined, 50);
      if (!entries.length) break;
      const first = entries[0]!;
      const late = (e: QueueEntry) => Date.now() - e.createdAt > OFFLINE_AFTER_MS;

      if (first.file) {
        const h = { ...first.file.headers, [FILE_HEADERS.sentAt]: new Date().toISOString(), [FILE_HEADERS.offline]: late(first) || first.item?.offline ? '1' : '0' };
        const res = await fetch(`/api/sync/files/${first.file.clientId}`, { method: 'PUT', body: first.file.blob, headers: h, credentials: 'same-origin' });
        if (res.status === 401) return markSignIn();
        if (res.status >= 500) throw new Error(`server ${res.status}`);
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          await record(first, body.message ?? `Upload refused (${res.status})`);
        }
        await d.delete('queue', first.seq!);
        emit();
        continue;
      }

      // Send consecutive JSON items together (stop at the next file to keep order).
      const batch: QueueEntry[] = [];
      for (const e of entries) {
        if (e.file) break;
        batch.push(e);
      }
      const items = batch.map((e) => ({ ...e.item!, offline: e.item!.offline || late(e) }));
      const res = await fetch('/api/sync', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ sentAt: new Date().toISOString(), items }),
      });
      if (res.status === 401) return markSignIn();
      if (!res.ok) throw new Error(`server ${res.status}`);
      const { results } = (await res.json()) as { results: SyncResult[] };
      for (const r of results) {
        const e = batch.find((b) => b.clientId === r.clientId)!;
        if (!r.ok && r.error?.code === 'UNAUTHORIZED') return markSignIn();
        if (!r.ok) await record(e, r.error?.message ?? 'Refused');
        await d.delete('queue', e.seq!);
      }
      emit();
      if (results.length < batch.length) throw new Error('partial batch');
    }
    backoff = 5_000;
    needsSignIn = false;
  } catch {
    // Offline or server trouble: keep everything, try again later.
    retryTimer = setTimeout(() => void flush(), backoff);
    backoff = Math.min(backoff * 2, 5 * 60_000);
  }
  emit();
}

function markSignIn() {
  needsSignIn = true;
  emit();
}

window.addEventListener('online', () => void flush());
